import React, { FC, useContext, useEffect, useRef, useState } from "react";
import styled, { css } from "styled-components";
import { stylesWrapper } from "@theme/commonStyles";
import { LanguageSelector } from "@components/atoms/LanguageSelector";
import { Button } from "@components/atoms/Button";
import { useI18next, useTranslation, Link } from "gatsby-plugin-react-i18next";
import { Hamburger } from "@components/atoms/Hamburger";
import { Logo } from "@components/atoms/Logo";
import { breakpoints } from "@theme/breakpoints";
import { ALL_ROUTES, Routes } from "@routes";
import { withLineHoverEffect } from "@theme/withLineHoverEffect";
import { SOCIAL_MEDIAS } from "@constants";
import { Icon } from "@iconify/react/offline";
import { cssEaseOutExpo, easeOutExpo } from "@theme/easing";
import { motion } from "framer-motion";
import { MainContext } from "@context";
import { useOnClickOutside } from "usehooks-ts";
import { graphql, useStaticQuery } from "gatsby";

const StyledWrapper = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  padding-top: 30px;
  width: 100%;
  z-index: 99999;

  ${breakpoints.phoneMax`
    padding-top: 20px;
  `};
`;

const StyledInnerWrapper = styled.div`
  ${stylesWrapper};
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-right: 75px;

  ${breakpoints.tabletMax`
    padding-right: 110px;
  `}

  ${breakpoints.phoneMax`
    padding-right: 75px;
  `}
`;

const StyledLogoWrapper = styled(motion.div)`
  margin-right: auto;
`;

const StyledLogoLink = styled(Link)`
  display: block;

  img {
    height: 75px;
    width: auto;
  }

  ${breakpoints.phoneMax`
    img {
      height: 55px;
    }
  `};
`;

const StyledRightWrapper = styled.nav`
  display: flex;
  align-items: center;
`;

const StyledRightAnimationWrapper = styled(motion.div)`
  display: flex;
  align-items: center;
`;

const StyledContactButton = styled(Button)`
  width: 180px;
  padding: 15px 0;
  margin-left: 30px;

  ${breakpoints.tabletMax`
    display: none;
  `};
`;

const StyledHamburgerWrapper = styled.div`
  ${stylesWrapper};
  position: fixed;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  height: 79px;
  left: 50%;
  transform: translateX(-50%);
  top: 30px;
  pointer-events: none;

  ${breakpoints.phoneMax`
    height: 58px;
    top: 20px;
  `}
`;

const StyledHamburgerWrapperAnimation = styled(motion.div)`
  pointer-events: all;
`;

const StyledMainMenuWrapper = styled.div<{ $isActive: boolean }>`
  position: fixed;
  left: 0;
  top: 0;
  z-index: -1;
  width: 100%;
  height: 100vh;
  pointer-events: none;
  background: transparent;
  transition: background 1.5s ${cssEaseOutExpo};

  ${({ $isActive }) =>
    $isActive &&
    css`
      background: rgba(0, 0, 0, 0.4);
      pointer-events: all;
    `}
`;

const StyledMainMenu = styled(motion.div)<{ $isHidden: boolean }>`
  width: 100%;
  height: 425px;
  background: ${({ theme }) => theme.background};
  display: flex;
  flex-direction: column;
  padding-top: 200px;
  padding-bottom: 50px;
  transform: translateY(-100%);
  visibility: ${({ $isHidden }) => ($isHidden ? "hidden" : "visible")};

  ${breakpoints.laptopMax`
    height: 400px;
    padding-top: 185px;
  `}

  ${breakpoints.tabletMax`
    height: unset;
    padding-top: 170px;
    max-height: 100vh;
    overflow-y: auto;
  `}


  ${breakpoints.phoneMax`
    padding-top: 120px;
    padding-bottom: 35px;
  `}
`;

const StyledMainMenuInnerWrapper = styled.div`
  ${stylesWrapper};
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const StyledList = styled.ul`
  margin: 0;
  padding: 0;
  list-style-type: none;
  display: flex;
  justify-content: space-between;

  ${breakpoints.tabletMax`
    flex-direction: column;
    align-items: center;
  `}

  ${breakpoints.phoneMax`
    align-items: flex-end;
  `}
`;

const StyledItem = styled.li<{ $isActive: boolean }>`
  font-size: 2.4rem;
  font-weight: 600;

  a {
    text-decoration: none;
    color: ${({ theme }) => theme.foreground};
    transition: color 0.2s ease-in-out;
  }

  :hover {
    a {
      color: ${({ theme }) => theme.primary};
    }
  }

  ${({ $isActive, theme }) =>
    $isActive
      ? css`
          &,
          &:hover {
            a {
              color: ${theme.primary};
              cursor: default;
            }
          }
        `
      : withLineHoverEffect({
          color: theme.primary,
          height: "3px",
          borderRadius: "2px",
          bottom: "-8px",
        })}}

  ${breakpoints.laptopMinMax`
    font-size: 2rem;
  `}

  ${breakpoints.tabletMax`
    margin-bottom: 30px;
    
    :last-child {
      margin-bottom: 0;
    }
  `}
  
  ${breakpoints.phoneMax`
    font-size: 2rem;
    margin-bottom: 25px;
  `}
`;

const StyledMainMenuBottom = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;

  ${breakpoints.tabletMax`
    margin-top: 100px;
  `}

  ${breakpoints.tabletMax`
    margin-top: 50px;
  `}
`;

const StyledMainMenuBottomRightWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const StyledCopyright = styled.div`
  display: flex;
  flex-direction: column;
  color: ${({ theme }) => theme.washMedium};
  font-size: 1.4rem;
  line-height: 1.5;
  margin-right: auto;

  span:first-child {
    font-weight: 600;
  }
`;

const StyledSocialMedias = styled.div`
  font-size: 2rem;
  color: ${({ theme }) => theme.washMedium};
  margin-left: 40px;

  a {
    margin-right: 20px;
    color: inherit;
    text-decoration: none;
    transition: color 0.2s ease-in-out;

    :last-child {
      margin-right: 0;
    }

    :hover {
      color: ${({ theme }) => theme.foreground};
    }
  }

  ${breakpoints.phoneMax`
    display: none;
  `}
`;

export const Navigation: FC<Props> = () => {
  const { t } = useTranslation();
  const { originalPath, language } = useI18next();
  const { strapiShared } = useStaticQuery(query);

  const [isActive, setActive] = useState<boolean>(false);
  const [isHidden, setHidden] = useState<boolean>(true);
  const [isContactSection, setContactSection] = useState<boolean>(true);
  const { isLoading, scroller } = useContext(MainContext);
  const mainMenu = useRef();
  useOnClickOutside(mainMenu, (event) => {
    if ((event.target as unknown as { id: string }).id === "main-menu") {
      setActive(false);
    }
  });

  const openMainMenu = t("navigation:open-menu");
  const closeMainMenu = t("navigation:close-menu");

  const animation = {
    initial: { y: "-100%", opacity: 0 },
    animate: !isLoading ? { y: 0, opacity: 1 } : {},
    transition:
      originalPath === "/"
        ? {
            delay: 0.8,
            ease: easeOutExpo,
            duration: 1,
          }
        : { delay: 0, ease: easeOutExpo, duration: 1 },
  };

  useEffect(() => {
    setContactSection(!!document.querySelector("#contact"));
  }, []);

  return (
    <StyledWrapper>
      <StyledInnerWrapper>
        <StyledLogoWrapper {...animation}>
          <StyledLogoLink to="/">
            <Logo />
          </StyledLogoLink>
        </StyledLogoWrapper>

        <StyledRightWrapper>
          <StyledRightAnimationWrapper {...animation}>
            <LanguageSelector />
            {isContactSection ? (
              <StyledContactButton
                onClick={() => {
                  const el = document.querySelector("#contact");
                  if (!el) return;
                  scroller?.toElement(el);
                  setActive(false);
                }}
              >
                {t("navigation:contact-button")}
              </StyledContactButton>
            ) : (
              <StyledContactButton
                forwardedAs={Link as any}
                to={Routes.Contact}
              >
                {t("navigation:contact-button")}
              </StyledContactButton>
            )}
          </StyledRightAnimationWrapper>

          <StyledHamburgerWrapper>
            <StyledHamburgerWrapperAnimation {...animation}>
              <Hamburger
                aria-expanded={isActive}
                aria-label={isActive ? closeMainMenu : openMainMenu}
                title={isActive ? closeMainMenu : openMainMenu}
                aria-controls="main-menu"
                toggled={isActive}
                onToggle={setActive}
              />
            </StyledHamburgerWrapperAnimation>
          </StyledHamburgerWrapper>

          <StyledMainMenuWrapper $isActive={isActive} id="main-menu">
            <StyledMainMenu
              animate={isActive ? { y: 0 } : { y: "-100%" }}
              ref={mainMenu}
              onAnimationComplete={() => {
                if (!isActive) {
                  setHidden(true);
                }
              }}
              onAnimationStart={() => {
                if (isActive) {
                  setHidden(false);
                }
              }}
              transition={{
                duration: 1.5,
                ease: easeOutExpo,
              }}
              initial={{ y: "-100%" }}
              $isHidden={isHidden}
            >
              <StyledMainMenuInnerWrapper>
                <StyledList>
                  {ALL_ROUTES.map(({ key, route: href }) => (
                    <StyledItem
                      key={key}
                      $isActive={
                        originalPath === "/"
                          ? originalPath === href
                          : originalPath.includes(href) && href != "/"
                      }
                    >
                      <Link to={href}>{t(`navigation:list.${key}`)}</Link>
                    </StyledItem>
                  ))}
                </StyledList>

                <StyledMainMenuBottom>
                  <StyledCopyright>
                    <span>&copy; {strapiShared.year} RendPro Limited</span>
                    <span>{t("common:copyright")}.</span>
                  </StyledCopyright>

                  <StyledMainMenuBottomRightWrapper>
                    {/*<ThemeToggle*/}
                    {/*  toggleDarkMode={toggleDarkMode}*/}
                    {/*  isDarkMode={isDarkMode}*/}
                    {/*/>*/}

                    <StyledSocialMedias>
                      {SOCIAL_MEDIAS(language).map(({ name, icon, href }) => (
                        <a
                          href={href}
                          target="_blank"
                          rel="noreferrer"
                          aria-label={name}
                          key={name}
                        >
                          <Icon icon={icon} />
                        </a>
                      ))}
                    </StyledSocialMedias>
                  </StyledMainMenuBottomRightWrapper>
                </StyledMainMenuBottom>
              </StyledMainMenuInnerWrapper>
            </StyledMainMenu>
          </StyledMainMenuWrapper>
        </StyledRightWrapper>
      </StyledInnerWrapper>
    </StyledWrapper>
  );
};

const query = graphql`
  {
    strapiShared {
      year
    }
  }
`;

interface Props {
  isDarkMode: boolean;
  toggleDarkMode: () => void;
}
